import { createFileRoute, Link } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Nkadimeng Danny Legal & Compliance Services" },
      {
        name: "description",
        content:
          "How we collect, use, store and protect your personal information in line with the Protection of Personal Information Act (POPIA).",
      },
      { property: "og:title", content: "Privacy Policy" },
      {
        property: "og:description",
        content: "How we handle your personal information under POPIA.",
      },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Last updated: 15 June 2026. This policy explains how Nkadimeng
            Danny Legal &amp; Compliance Services (&quot;we&quot;, &quot;us&quot;) collects, uses,
            and protects your personal information.
          </p>
        </div>
      </section>

      <section className="container-narrow py-12 md:py-16 space-y-8 text-sm md:text-base leading-relaxed text-foreground/90">
        <Block title="1. Who we are">
          <p>
            Nkadimeng Danny is the responsible party (data controller) for
            personal information processed through this website and our
            services. You can contact us at{" "}
            <a href={`mailto:${site.email}`} className="text-navy underline hover:text-gold">
              {site.email}
            </a>{" "}
            or {site.phone}.
          </p>
        </Block>

        <Block title="2. Information we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li>Identity &amp; contact: name, email, phone number, location.</li>
            <li>Matter details you choose to share when booking or enquiring.</li>
            <li>Payment status (we do not store full card details).</li>
            <li>Technical data: IP address, device, browser, usage analytics.</li>
            <li>Communications you send to us (email, WhatsApp, SMS).</li>
          </ul>
        </Block>

        <Block title="3. How we use your information">
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide the legal-support service you requested.</li>
            <li>Confirm bookings and send appointment reminders by email and SMS.</li>
            <li>Issue invoices and keep accounting records as required by law.</li>
            <li>Improve the website and our service quality.</li>
            <li>Comply with statutory and regulatory obligations.</li>
          </ul>
        </Block>

        <Block title="4. Lawful basis (POPIA)">
          <p>
            We process your personal information on one or more of the
            following lawful bases under section 11 of POPIA: your consent,
            performance of a contract with you, compliance with a legal
            obligation, protection of a legitimate interest, or pursuit of our
            legitimate interests balanced against your rights.
          </p>
        </Block>

        <Block title="5. Sharing">
          <p>
            We do not sell your personal information. We share it only with
            trusted processors who help us deliver the service (email and SMS
            providers, secure cloud hosting, payment processors, accounting),
            with admitted attorneys you have asked us to refer you to, or
            where required by law.
          </p>
        </Block>

        <Block title="6. Retention">
          <p>
            We keep personal information only for as long as needed to
            deliver the service, comply with legal duties (typically up to
            five years for financial records), or resolve disputes.
          </p>
        </Block>

        <Block title="7. Security">
          <p>
            We use reasonable technical and organisational safeguards —
            encryption in transit, role-based access, secure cloud
            infrastructure and audit logging — to protect your information.
          </p>
        </Block>

        <Block title="8. Your rights">
          <ul className="list-disc pl-5 space-y-1">
            <li>Request access to the personal information we hold about you.</li>
            <li>Request correction or deletion.</li>
            <li>Object to processing or withdraw consent at any time.</li>
            <li>Lodge a complaint with the Information Regulator of South Africa.</li>
          </ul>
        </Block>

        <Block title="9. Contact">
          <p>
            To exercise any right or raise a privacy concern, email{" "}
            <a href={`mailto:${site.email}`} className="text-navy underline hover:text-gold">
              {site.email}
            </a>
            . See our{" "}
            <Link to="/popia" className="text-navy underline hover:text-gold">
              POPIA policy
            </Link>{" "}
            for further detail and our{" "}
            <Link to="/complaints" className="text-navy underline hover:text-gold">
              complaints procedure
            </Link>.
          </p>
        </Block>
      </section>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-navy">{title}</h2>
      <div className="mt-3 text-muted-foreground">{children}</div>
    </div>
  );
}
